import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useCallback, useState } from 'react';
import { ApiError } from '../lib/errors';
import { timeEntryService } from '../services/timeEntryService';

const MAX_LISTED = 5;

const formatDays = (days: string[]) => {
  const shown = days.slice(0, MAX_LISTED).map((d) => format(parseISO(d), 'EEE dd MMM'));
  return days.length > MAX_LISTED ? `${shown.join(', ')}, …` : shown.join(', ');
};

export function useCloseWeek(employeeId?: string) {
  const queryClient = useQueryClient();
  const [closingWeek, setClosingWeek] = useState<string | null>(null);

  const closeWeek = useCallback(
    async (calendarWeek: string): Promise<boolean> => {
      if (!employeeId || closingWeek) return false;
      setClosingWeek(calendarWeek);
      try {
        const preview = await timeEntryService.previewCloseWeek(employeeId, calendarWeek);

        if (preview.mismatch_days.length > 0) {
          alert(
            `Cannot close ${calendarWeek}: logged hours and project hours don't match on ${formatDays(preview.mismatch_days)}.`
          );
          return false;
        }

        const lines = [`Are you sure you want to close all entries for calendar week ${calendarWeek}?`];
        if (preview.missing_days.length > 0) {
          lines.push(`No entries yet: ${formatDays(preview.missing_days)}.`);
        }
        lines.push('This action cannot be undone.');
        if (!window.confirm(lines.join('\n\n'))) return false;

        await timeEntryService.closeWeekEntries(employeeId, calendarWeek);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['timeEntries'] }),
          queryClient.invalidateQueries({ queryKey: ['monthlyStatus'] }),
        ]);
        alert(`${calendarWeek} closed.`);
        return true;
      } catch (error) {
        const days: string[] | undefined =
          error instanceof ApiError && error.status === 409 ? error.data?.data?.mismatch_days : undefined;
        if (days?.length) {
          alert(`Cannot close ${calendarWeek}: logged hours and project hours don't match on ${formatDays(days)}.`);
        } else {
          alert(`Failed to close week entries: ${error instanceof Error ? error.message : String(error)}`);
        }
        return false;
      } finally {
        setClosingWeek(null);
      }
    },
    [employeeId, closingWeek, queryClient]
  );

  return { closeWeek, closingWeek };
}

// Read-only preview for the selected week. `version` must change whenever entries are saved
// (TimeTracking passes when the month status was last fetched), so the preview refreshes after every edit.
export function useWeekClosePreview(employeeId?: string, calendarWeek?: string, version?: number) {
  const { data, isLoading } = useQuery({
    queryKey: ['timeEntries', calendarWeek, employeeId, 'closePreview', version],
    queryFn: () => timeEntryService.previewCloseWeek(employeeId!, calendarWeek!),
    enabled: !!employeeId && !!calendarWeek,
    retry: false,
  });
  const mismatchDays = data?.mismatch_days ?? [];
  return {
    isLoading,
    hasMismatch: mismatchDays.length > 0,
    mismatchReason: mismatchDays.length
      ? `Logged and project hours don't match on ${formatDays(mismatchDays)}`
      : undefined,
  };
}
